import React, { useContext, useEffect, useMemo, useState } from "react";
import { ApolloError } from "@apollo/client";
import { captureException } from "@sentry/browser";
import { isEmpty, omit } from "lodash";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory, useLocation, useParams } from "react-router";
import { buildLink } from "common/routing";
import { buildFormStructure } from "common/services/formBuilderService";
import {
  checkForPromoteSubmissionsBreakingChanges,
  checkForSavedViewsBreakingChanges,
} from "common/utils/submissions";
import { getUserErrors } from "common-client/utils/apollo";
import {
  CreateSubmissionTypeInput,
  GetSubmissionTypeForBuilderQuery,
  RoleName,
  useCreateSubmissionTypeMutation,
  useGetSubmissionsSavedViewsQuery,
  useGetSubmissionTypeForBuilderQuery,
  useUpdateSubmissionTypeMutation,
} from "../../../../../generated/graphql";
import { useStatusToasts } from "../../../../../hooks/useStatusToasts";
import { AuthContext } from "../../../../Authorization/AuthContext";
import { AuthContextType } from "../../../../Authorization/types";
import FullPageFormLayout from "../../../../Common/FullPageFormLayout";
import FullPageLoadingScreen from "../../../../Common/FullPageLoadingScreen";
import { SubmissionsBuilderWrapper } from "./__styles__/SubmissionsBuilder";
import useSubmissionsBuilderChatAgent from "./BuilderChatAgent";
import { SubmissionsBuilderContextInstance } from "./context";
import {
  CreateSubmissionTypeFormStructure,
  useCreateSubmissionTypeModal,
} from "./createSubmissionTypeModal";
import resolver from "./formResolver";
import { HeaderMiddleContainer } from "./HeaderMiddleContainer";
import { onCanCreateChange } from "./helpers";
import { MainColumn } from "./MainColumn/MainColumn";
import { MenuRightButtons } from "./MenuRightButtons";
import { usePromoteSubmissionsModal } from "./PromoteSubmissionsModal";
import { RightSidebarColumn } from "./RightSidebarColumn";
import { SidebarColumn } from "./SidebarColumn/SidebarColumn";
import SupportedMobileVersionsContainer from "./SupportedMobileVersionsContainer";
import { SubmissionsBuilderFormDataStructure } from "./types";
import useSubmissionsBuilderContextManager from "./useSubmissionsBuilderContextManager";
import {
  buildNotificationEmailsArray,
  generateDefaultValues,
  getValidPostamble,
  getValidPreamble,
} from "./utils";
import { extractValidationErrors } from "./validationErrorsUtils";

const formatData = ({
  formData,
  canCreateGroups,
  canCreateRoles,
}: {
  formData: SubmissionsBuilderFormDataStructure;
  canCreateGroups: { id: string; name: string; memberType: string }[];
  canCreateRoles: Array<RoleName>;
}): Omit<CreateSubmissionTypeInput, "formStructure"> => {
  return {
    ...omit(formData, [
      "inputs",
      "notificationEmails",
      "preamble",
      "postamble",
      "canCreateRolesAndGroupIds",
    ]),
    postamble: getValidPostamble({
      postamble: formData.postamble,
      canCreateRoles,
      canCreateGroups,
    }),
    preamble: getValidPreamble({
      preamble: formData.preamble,
      canCreateRoles,
      canCreateGroups,
    }),
    notificationEmails: buildNotificationEmailsArray({
      notificationEmails: formData.notificationEmails,
      canCreateRoles,
      canCreateGroups,
    }),
    canCreate: canCreateRoles,
    canCreateGroupIds: canCreateGroups.map(userGroup => userGroup.id),
  };
};

type BuilderFormLayoutContentProps = {
  subtitle: string;
  prevLocation: string;
  existingSubmissionType?: GetSubmissionTypeForBuilderQuery["submissionType"];
  account: AuthContextType["account"];
  isRightSideBarOpen: boolean;
  openRightSideBar: (selectedVersionId: string) => void;
  closeRightSideBar: () => void;
  loadingSavedViewData: boolean;
  createLoading: boolean;
  updateLoading: boolean;
  isValid: boolean;
  isDirty: boolean;
  validationErrors: { path: string; message: string }[];
  onSave: () => void;
};

const BuilderFormLayoutContent: React.FC<BuilderFormLayoutContentProps> = ({
  subtitle,
  prevLocation,
  existingSubmissionType,
  account,
  isRightSideBarOpen,
  openRightSideBar,
  closeRightSideBar,
  loadingSavedViewData,
  createLoading,
  updateLoading,
  isValid,
  isDirty,
  validationErrors,
  onSave,
}) => {
  const { ChatToggleButton: BuilderChatToggle, ChatPanel: BuilderChatPanel } =
    useSubmissionsBuilderChatAgent({ account, existingSubmissionType });

  return (
    <FullPageFormLayout
      subtitle={subtitle}
      prevLocation={prevLocation}
      rightContainer={
        <MenuRightButtons
          isRightSideBarOpen={isRightSideBarOpen}
          openRightSideBar={openRightSideBar}
          closeRightSideBar={closeRightSideBar}
          versions={existingSubmissionType?.versions ?? []}
          onSave={onSave}
          disabled={
            loadingSavedViewData || createLoading || updateLoading || !isValid
          }
          formIsDirty={isDirty}
          validationErrors={validationErrors}
        >
          {BuilderChatToggle}
        </MenuRightButtons>
      }
      middleContainer={<HeaderMiddleContainer />}
      centered={false}
      width="auto"
    >
      <SubmissionsBuilderWrapper hasRightSidebar={isRightSideBarOpen}>
        <SidebarColumn />
        <MainColumn />
        {isRightSideBarOpen && (
          <RightSidebarColumn onClose={closeRightSideBar} />
        )}
      </SubmissionsBuilderWrapper>
      {BuilderChatPanel}
    </FullPageFormLayout>
  );
};

const SubmissionsBuilder = ({
  existingSubmissionType,
}: {
  existingSubmissionType?: GetSubmissionTypeForBuilderQuery["submissionType"];
}) => {
  const history = useHistory();
  const { addErrorToast, addSuccessToast } = useStatusToasts();
  const { account } = useContext(AuthContext);
  const [isRightSideBarOpen, setIsRightSideBarOpen] = useState(false);
  const [
    promoteSubmissionsBreakingChanges,
    setPromoteSubmissionsBreakingChanges,
  ] = useState<{
    updatedToRequiredFields: string[];
  } | null>(null);
  const { data: submissionsSavedViewsData, loading: loadingSavedViewData } =
    useGetSubmissionsSavedViewsQuery({
      variables: { accountId: account!.id },
      skip: !existingSubmissionType,
      fetchPolicy: "cache-and-network",
    });

  const accountUserGroups = account?.userGroups ?? [];

  const submissionsSavedViews =
    submissionsSavedViewsData?.account?.submissionsSavedViews;

  const location = useLocation<undefined | { prevLocation?: string }>();
  const prevLocation =
    location.state?.prevLocation ?? "/settings/account/records";

  const startingSelectedGroups = accountUserGroups.filter(userGroup =>
    existingSubmissionType?.canCreateGroups.find(
      canCreateGroup => canCreateGroup.id === userGroup.id,
    ),
  );

  const context = useSubmissionsBuilderContextManager({
    submissionTypeVersions: existingSubmissionType?.versions,
    readOnly: isRightSideBarOpen,
    currentVersionId: existingSubmissionType?.currentVersion.id,
    startingCanCreateGroups: startingSelectedGroups,
    startingCanCreateRoles: existingSubmissionType?.canCreate ?? [],
  });

  const defaultValues = useMemo(
    () =>
      generateDefaultValues({
        existingSubmissionType,
        versionId: context.selectedVersionId,
      }),
    [existingSubmissionType, context.selectedVersionId],
  );

  const formMethods = useForm<SubmissionsBuilderFormDataStructure>({
    // @ts-ignore -- until we get all other input types finished
    defaultValues,
    context,
    resolver,
    mode: "onChange",
  });

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    trigger,
    formState: { isDirty, isValid, errors },
  } = formMethods;

  const formData = watch();

  const validationErrors = React.useMemo(
    () => extractValidationErrors(errors, formData),
    [errors, formData],
  );

  useEffect(() => {
    // @ts-expect-error -- If it's a new form, Attachments could be an empty arry,
    // which doesn't match the type for the form.
    reset(defaultValues);

    void trigger();
  }, [defaultValues, reset, trigger]);

  const onCompleted = (verb: "created" | "updated") => {
    addSuccessToast(`Record type ${verb}`);
    history.push(buildLink("records"));
  };

  const onError = (error: ApolloError) => {
    const userErrors = getUserErrors(error);

    if (userErrors) {
      addErrorToast(userErrors);
    } else {
      addErrorToast("Could not save record type. Please try again.");
    }
  };

  const [createSubmissionType, { loading: createLoading }] =
    useCreateSubmissionTypeMutation({
      onCompleted: () => onCompleted("created"),
      onError,
    });

  const [updateSubmissionType, { loading: updateLoading }] =
    useUpdateSubmissionTypeMutation({
      onCompleted: () => onCompleted("updated"),
      onError,
    });

  const [showCreateSubmissionTypeModal] = useCreateSubmissionTypeModal({
    context,
    onSubmit: ({
      name,
      category,
      attachments,
      canCreateRolesAndGroupIds,
    }: CreateSubmissionTypeFormStructure) => {
      setValue("name", name);
      setValue("category", category);
      setValue("canCreateRolesAndGroupIds", canCreateRolesAndGroupIds);
      setValue("attachments", attachments);
      onCanCreateChange({
        getFormData: formMethods.getValues,
        setFormData: formMethods.setValue,
        setCanCreateGroups: context.setCanCreateGroups,
        setCanCreateRoles: context.setCanCreateRoles,
        accountUserGroups,
      });
    },
  });

  useEffect(() => {
    if (!existingSubmissionType) {
      showCreateSubmissionTypeModal();
    }
  }, [showCreateSubmissionTypeModal, existingSubmissionType]);

  const formName = watch("name");

  const subtitle = formName ? `${formName} form` : "Add record type";

  const [showPromoteSubmissionsModal, hidePromoteSubmissionsModal] =
    usePromoteSubmissionsModal({
      onSubmit: async ({
        promoteSubmissions,
      }: {
        promoteSubmissions: string;
      }) => {
        setValue("promoteSubmissions", promoteSubmissions === "true");

        // Get the current form data
        const formData = formMethods.getValues();

        const formStructure = buildFormStructure({
          formData: formData.inputs!,
        });
        const formattedData = formatData({
          formData,
          canCreateGroups: context.canCreateGroups,
          canCreateRoles: context.canCreateRoles,
        });
        const updatedFormStructure = omit(formStructure, ["isValid", "errors"]);

        // Perform the update mutation
        const updateData = omit(formattedData, ["modules"]);
        await updateSubmissionType({
          variables: {
            data: {
              ...updateData,
              formStructure: updatedFormStructure,
              submissionTypeId: existingSubmissionType!.id,
              submissionTypeVersionId:
                context.selectedVersionId ??
                existingSubmissionType!.currentVersion.id,
              promoteSubmissions: promoteSubmissions === "true",
            },
          },
        });
      },
      onCancel: () => {
        hidePromoteSubmissionsModal();
      },
      breakingChanges: promoteSubmissionsBreakingChanges ?? undefined,
    });

  const handleSubmitCallback = async (
    formData: SubmissionsBuilderFormDataStructure,
  ) => {
    const formStructure = buildFormStructure({ formData: formData.inputs! });

    if (!formStructure.isValid) {
      let errorMessage =
        existingSubmissionType ?
          `formStructure for existing submission type (id=${existingSubmissionType.id})`
        : "formStructure";

      errorMessage += `is invalid. Please address the following errors:\n${formStructure.errors.join(
        "\n",
      )}`;

      throw new Error(errorMessage);
    }

    const formattedData = formatData({
      formData,
      canCreateGroups: context.canCreateGroups,
      canCreateRoles: context.canCreateRoles,
    });

    const updatedFormStructure = omit(formStructure, ["isValid", "errors"]);

    if (existingSubmissionType) {
      const savedViewBreakingChanges = checkForSavedViewsBreakingChanges({
        submissionTypeId: existingSubmissionType.id,
        submissionTypeName: existingSubmissionType.name,
        previousFormStructure: existingSubmissionType.versions.find(
          version => version.id === context.selectedVersionId,
        )!.formStructure,
        currentFormStructure: updatedFormStructure,
        submissionSavedViews: submissionsSavedViews ?? [],
        category: existingSubmissionType.category,
        onError: error => {
          captureException(error);
        },
      });

      if (isEmpty(savedViewBreakingChanges)) {
        // Check for promote submissions breaking changes
        const promoteSubmissionsBreakingChanges =
          checkForPromoteSubmissionsBreakingChanges({
            previousFormStructure: existingSubmissionType.versions.find(
              version => version.id === context.selectedVersionId,
            )!.formStructure,
            currentFormStructure: updatedFormStructure,
            submissionTypeId: existingSubmissionType.id,
            submissionTypeName: existingSubmissionType.name,
            category: existingSubmissionType.category,
            onError: error => {
              captureException(error);
            },
          });

        // Set the breaking changes state
        setPromoteSubmissionsBreakingChanges(promoteSubmissionsBreakingChanges);

        // Show the promote submissions modal for existing submissions
        showPromoteSubmissionsModal();
      } else {
        addErrorToast(
          "Updating this record will break currently saved table views. Modify these views before making changes to the record.",
        );
      }
    } else {
      await createSubmissionType({
        variables: {
          data: {
            ...formattedData,
            formStructure: updatedFormStructure,
          },
        },
      });
    }
  };

  const openRightSideBar = (selectedVersionId: string) => {
    setIsRightSideBarOpen(true);
    context.setSelectedVersionId(selectedVersionId);
  };

  const closeRightSideBar = () => {
    context.setSelectedVersionId(
      existingSubmissionType?.currentVersion.id ?? null,
    );
    setIsRightSideBarOpen(false);
  };

  return (
    <SubmissionsBuilderContextInstance.Provider value={context}>
      <SupportedMobileVersionsContainer>
        <FormProvider {...formMethods}>
          <BuilderFormLayoutContent
            subtitle={subtitle}
            prevLocation={prevLocation}
            existingSubmissionType={existingSubmissionType}
            account={account}
            isRightSideBarOpen={isRightSideBarOpen}
            openRightSideBar={openRightSideBar}
            closeRightSideBar={closeRightSideBar}
            loadingSavedViewData={loadingSavedViewData}
            createLoading={createLoading}
            updateLoading={updateLoading}
            isValid={isValid}
            isDirty={isDirty}
            validationErrors={validationErrors}
            onSave={handleSubmit(handleSubmitCallback)}
          />
        </FormProvider>
      </SupportedMobileVersionsContainer>
    </SubmissionsBuilderContextInstance.Provider>
  );
};

const SubmissionBuilderWrapper = () => {
  const { submissionTypeId } = useParams<{ submissionTypeId?: string }>();

  const { data, error, loading } = useGetSubmissionTypeForBuilderQuery({
    fetchPolicy: "cache-and-network",
    variables: { submissionTypeId: submissionTypeId! },
    skip: !submissionTypeId,
  });

  if (error) {
    return <em>There was a problem loading your submission</em>;
  }

  if (loading) {
    return <FullPageLoadingScreen />;
  }

  return <SubmissionsBuilder existingSubmissionType={data?.submissionType} />;
};

export default SubmissionBuilderWrapper;
